/**
 * Deploy 3 · P1 — mover una cita (drag) con la verdad del backend: reschedule → 409 →
 * vista previa de cascada → aplicar. Toast "Deshacer" tras cada movimiento.
 */
import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { bookingApi } from '@/lib/bookingApi';
import { isScheduleConflict, conflictRules } from '@/components/Profile/ScheduleConflictDialog';
import { formatTime12 } from '@/utils/bookingFormat';
import { tr } from '@/contexts/LanguageContext';

const toMin = (hhmm) => { const [h, m] = hhmm.split(':').map(Number); return h * 60 + m; };
const toHHMM = (v) => `${String(Math.floor(v / 60)).padStart(2, '0')}:${String(v % 60).padStart(2, '0')}`;
const undoToast = (label, onUndo) => toast.success(label, {
  duration: 8000,
  action: { label: tr("Deshacer"), onClick: onUndo },
});

export const useBookingMove = ({ selectedDay, schedule, reload, patchLocal }) => {
  const [saving, setSaving] = useState(false);
  const [cascade, setCascade] = useState(null);
  // B3 — conflicto: el barbero decide «Continuar» (encimar) o «Atrás».
  const [conflict, setConflict] = useState(null);

  const confirmOverlap = useCallback(async () => {
    const c = conflict;
    if (!c) return;
    setSaving(true);
    try {
      await bookingApi.rescheduleBooking(c.booking.booking_id, selectedDay, c.newStart, null, 'move', true);
      setConflict(null);
      await reload();
      toast.success(tr("Cita movida a {0} (encimada)", formatTime12(c.newStart)));
    } catch (err) {
      toast.error(err?.message || tr("No se pudo mover la cita"));
      setConflict(null);
      await reload();
    } finally {
      setSaving(false);
    }
  }, [conflict, selectedDay, reload]);

  const moveBooking = useCallback(async (booking, newStartMin) => {
    const dur = toMin(booking.end) - toMin(booking.start);
    const newStart = toHHMM(newStartMin);
    const postCharge = booking.status === 'completed';
    setSaving(true);
    // Optimista (rendimiento D): la tarjeta se mueve al instante; si el backend rechaza, reload() la devuelve.
    patchLocal?.(booking.booking_id, { start: newStart, end: toHHMM(newStartMin + dur) });
    try {
      await bookingApi.rescheduleBooking(booking.booking_id, selectedDay, newStart, null, 'move');
      await reload();
      undoToast(postCharge
        ? tr("Horario editado a {0} — el cobro y Mi Dinero no cambian", formatTime12(newStart))
        : tr("Cita movida a {0}", formatTime12(newStart)), async () => {
        try {
          await bookingApi.rescheduleBooking(booking.booking_id, selectedDay, booking.start, null, 'move');
          toast.success(tr("Cita devuelta a {0}", formatTime12(booking.start)));
        } catch (err) {
          toast.error(err?.message || tr("No se pudo deshacer"));
        }
        await reload();
      });
    } catch (err) {
      await reload(); // rollback visual = verdad del backend
      // D5 · B: una cita completada nunca dispara cascada (el servicio ya se prestó).
      if (err?.status === 409 && !postCharge && schedule?.cascade_move_enabled !== false) {
        try {
          const plan = await bookingApi.cascadePreview(booking.booking_id, selectedDay, newStart, dur);
          if (plan?.feasible && (plan.moves || []).length > 1) {
            setCascade({ plan, target: { booking, date: selectedDay, start: newStart, duration: dur } });
            return;
          }
          if (plan && !plan.feasible && plan.reason && !isScheduleConflict(err)) { toast.error(plan.reason); return; }
        } catch { /* cascada desactivada → mostrar el 409 original */ }
      }
      if (isScheduleConflict(err) && !postCharge) { setConflict({ booking, newStart, rules: conflictRules(err) }); return; }
      toast.error(err?.message || tr("No se pudo mover la cita"));
    } finally {
      setSaving(false);
    }
  }, [selectedDay, schedule, reload]);

  const confirmCascade = useCallback(async () => {
    const t = cascade?.target;
    if (!t) return;
    const moves = cascade.plan.moves;
    setSaving(true);
    try {
      const res = await bookingApi.cascadeApply(t.booking.booking_id, t.date, t.start, t.duration);
      setCascade(null);
      await reload();
      undoToast(tr("{0} citas reubicadas", res.applied), async () => {
        try {
          // La primaria primero (libera el hueco), luego las empujadas en orden.
          for (const mv of moves) {
            await bookingApi.rescheduleBooking(mv.booking_id, mv.from_date || t.date, mv.from_start, null, 'move');
          }
          toast.success(tr("Movimiento en cascada deshecho"));
        } catch (err) {
          toast.error(err?.message || tr("No se pudo deshacer todo — revisa la agenda"));
        }
        await reload();
      });
    } catch (err) {
      toast.error(err?.message || tr("No se pudo aplicar el movimiento"));
      setCascade(null);
      await reload();
    } finally {
      setSaving(false);
    }
  }, [cascade, reload]);

  return { saving, cascade, setCascade, moveBooking, confirmCascade, conflict, setConflict, confirmOverlap };
};
